<script setup>
import {ref} from 'vue';
import {Dialog, DialogPanel, DialogTitle, TransitionRoot} from '@headlessui/vue';
import {useI18n} from 'vue-i18n';

/**
 * IMA-517: nový dokument k emisi dluhopisu (se souborem PDF) nebo úprava existujícího
 * (název, typ, „přiložit ke klientovi“ – soubor se mění jen nahráním nové verze).
 */
const {t} = useI18n();

const props = defineProps({
  document: {type: Object, required: false, default: null},
});
const emit = defineEmits(['confirm', 'cancel']);
const isOpen = ref(true);
const isEdit = !!props.document;

const documentTypes = ['EMISSION_TERMS', 'PROSPECTUS', 'FINAL_TERMS', 'OTHER'];
const form = ref({
  name: props.document?.name ?? '',
  bond_document_type: props.document?.bond_document_type ?? 'EMISSION_TERMS',
  attach_to_client: props.document?.attach_to_client ?? true,
  file: null,
});

function onFileChange(event) {
  form.value.file = event.target.files[0] ?? null;
  // název předvyplnit z názvu souboru, pokud ho uživatel nezadal
  if (form.value.file && !form.value.name) {
    form.value.name = form.value.file.name.replace(/\.pdf$/i, '');
  }
}

const closeModalWith = (value) => {
  setTimeout(() => {
    if (value === 'confirm') {
      const data = {...form.value};
      if (isEdit) {
        delete data.file;
      }
      emit('confirm', data);
    } else {
      emit('cancel');
    }
    isOpen.value = false;
  }, 300);
};
</script>

<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div" @close="closeModalWith('cancel')" class="modal show d-block" style="z-index: 1055;">
      <div class="modal-backdrop fade show"></div>
      <div class="modal-dialog modal-dialog-centered" style="z-index: 1055">
        <div class="modal-content">
          <DialogPanel class="modal-body p-4 rounded-lg shadow-lg">
            <div class="modal-header">
              <DialogTitle as="h3" class="modal-title">{{ isEdit ? t('bondDocuments.edit_document') : t('bondDocuments.upload_document') }}</DialogTitle>
              <i class="btn-close cursor-pointer" @click="closeModalWith('cancel')" aria-label="Close"></i>
            </div>
            <form @submit.prevent="closeModalWith('confirm')">
              <div class="modal-body">
                <div v-if="!isEdit" class="mb-3 form-group">
                  <label for="bondDocumentFile" class="form-label">{{ t('bondDocuments.file') }}</label>
                  <input id="bondDocumentFile" type="file" accept="application/pdf,.pdf" class="form-control" required @change="onFileChange"/>
                </div>
                <div class="mb-3 form-group">
                  <label for="bondDocumentName" class="form-label">{{ t('bondDocuments.name') }}</label>
                  <input id="bondDocumentName" v-model="form.name" class="form-control" maxlength="255" required/>
                </div>
                <div class="mb-3 form-group">
                  <label for="bondDocumentType" class="form-label">{{ t('bondDocuments.type') }}</label>
                  <select id="bondDocumentType" v-model="form.bond_document_type" class="form-select">
                    <option v-for="type in documentTypes" :key="type" :value="type">{{ t('bondDocuments.types.' + type) }}</option>
                  </select>
                </div>
                <div class="form-check">
                  <input id="bondDocumentAttach" v-model="form.attach_to_client" type="checkbox" class="form-check-input"/>
                  <label for="bondDocumentAttach" class="form-check-label">{{ t('bondDocuments.attach_to_client') }}</label>
                </div>
                <small class="text-muted">{{ t('bondDocuments.attach_to_client_hint') }}</small>
              </div>
              <div class="modal-footer">
                <button type="button" class="btn btn-link text-decoration-underline text-dark" @click="closeModalWith('cancel')">{{ t('createUpdateStrategyModal.cancel') }}</button>
                <button class="btn btn-primary btn-md" type="submit">{{ t('createUpdateStrategyModal.save') }}</button>
              </div>
            </form>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
